import { useHotkey } from '@modules/common/hooks';
import { CheckRounded, CloseRounded, EditRounded } from '@mui/icons-material';
import {
  Box,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

interface EditableCompareFilterFieldProps {
  initialFieldName: string;
  onSave: (filterName: string) => void;
}

export const EditableCompareFieldName = ({
  initialFieldName,
  onSave,
}: EditableCompareFilterFieldProps) => {
  const [isEditFieldNameMode, setIsEditFieldNameMode] = useState(false);
  const [compareFieldName, setCompareFieldName] = useState(initialFieldName);

  useEffect(() => {
    setCompareFieldName(initialFieldName);
  }, [initialFieldName]);

  const onCancel = () => {
    setCompareFieldName(initialFieldName);
    setIsEditFieldNameMode(false);
  };

  useHotkey({
    hotkey: 'Escape',
    onHotkey: onCancel,
  });

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!compareFieldName) {
      setCompareFieldName(initialFieldName);
    }

    onSave(compareFieldName);
    setIsEditFieldNameMode(false);
  };

  if (!isEditFieldNameMode) {
    return (
      <Stack spacing={1} direction="row" alignItems="center">
        <Typography variant="body2">{initialFieldName}</Typography>
        <IconButton
          size="small"
          sx={{
            color: 'text.secondary',
          }}
          onClick={() => setIsEditFieldNameMode(true)}
          aria-label="edit-compare-field-name-button"
        >
          <EditRounded
            sx={{
              fontSize: (theme) => theme.typography.body1.fontSize,
            }}
          />
        </IconButton>
      </Stack>
    );
  }

  return (
    <Box component="form" onSubmit={onSubmit}>
      <TextField
        autoFocus
        fullWidth
        size="small"
        value={compareFieldName}
        onChange={(e) => setCompareFieldName(e.target.value)}
        placeholder="Enter a name"
        required
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={onCancel}
                aria-label="cancel-compare-field-name-button"
              >
                <CloseRounded fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={onSubmit}
                aria-label="save-compare-field-name-button"
                disabled={!compareFieldName.trim()}
              >
                <CheckRounded fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
};
